import React, { useEffect, useState, useRef } from "react";

import {
  UserRound,
  CheckCircle2,
  XCircle,
  Ban,
  AlertCircle,
  Share2,
  Link2,
} from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { useTranslation } from "react-i18next";
import { FaWhatsapp, FaLinkedin } from "react-icons/fa";
import { useParams } from "react-router-dom";

import CfLoader from "../../common/CfLoader/CfLoader";
import CredentialStatusDetailsModal from "../../common/CredentialStatusDetailsModal/CredentialStatusDetailsModal";
import EvidenceModal from "../../common/EvidenceModal/EvidenceModal";
import ImagePreviewModal from "../../common/ImagePreviewModal/ImagePreviewModal";
import renderPdf from "../../common/RenderPdf/RenderPdf";
import Toast from "../../common/Toast/Toast";
import CertificateArtwork from "../../components/CertificateArtwork/CertificateArtwork";
import { DownloadIcon } from "../../components/icons";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import {
  getCredentialStatusTone,
  normalizeCredentialStatus,
  STATUS_TONE_STYLES,
} from "../../utils/credentialStatusUtils";

const CredentialPreview = () => {
  const { cert_hash } = useParams();
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMarksheets, setShowMarksheets] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showStatusDetails, setShowStatusDetails] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [headerLogoVisible, setHeaderLogoVisible] = useState(true);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [resolvedStatus, setResolvedStatus] = useState(null);

  const pdfCanvasRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const shareMenuRef = useRef(null);
  const shareButtonRef = useRef(null);

  const { t } = useTranslation();

  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isShareMenuOpen &&
        shareMenuRef.current &&
        !shareMenuRef.current.contains(event.target) &&
        shareButtonRef.current &&
        !shareButtonRef.current.contains(event.target)
      ) {
        setIsShareMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isShareMenuOpen]);

  // Sync left/right panel heights
  useEffect(() => {
    const leftEl = leftPanelRef.current;
    if (!leftEl) return;

    const syncHeight = () => {
      if (rightPanelRef.current && window.innerWidth >= 768) {
        rightPanelRef.current.style.maxHeight = `${leftEl.offsetHeight}px`;
      } else if (rightPanelRef.current) {
        rightPanelRef.current.style.maxHeight = "none";
      }
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(leftEl);
    window.addEventListener("resize", syncHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncHeight);
    };
  }, [certificateData]);

  const fetchCertificateData = async () => {
    try {
      const response = await GET(
        `${EntityConstants.GET_CERTIFICATE_PREVIEW}?encoded_data=${cert_hash}&format=json`,
      );

      const responseData = await response.json();
      if (response.ok) {
        setCertificateData(responseData);
        if (
          responseData.pdf_url &&
          (responseData.pdf_url.endsWith(".pdf") ||
            responseData.pdf_url.includes(".pdf?"))
        ) {
          setIsPdf(true);
          setIsPdfLoading(true);
          renderPdf(responseData.pdf_url, pdfCanvasRef).finally(() =>
            setIsPdfLoading(false),
          );
        }
      } else {
        console.log(
          responseData?.error || responseData?.message || responseData?.detail,
        );
        setError(`${t("history:something_went_wrong_retry")}`);
        const errorMessage =
          responseData?.error ||
          responseData?.message ||
          responseData?.detail ||
          "An unknown error occurred";
        Toast({
          type: "error",
          title: t("history:error"),
          message: errorMessage,
        });
      }
    } catch (err) {
      setError(`${t("history:something_went_wrong_retry")}`);
      Toast({
        type: "error",
        title: t("history:error"),
        message: err?.message || `${t("history:failed_load_certificate_data")}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    const isPdfDownload =
      !!certificateData?.pdf_url &&
      (certificateData.pdf_url.endsWith(".pdf") ||
        certificateData.pdf_url.includes(".pdf?"));

    const downloadUrl = isPdfDownload
      ? certificateData?.pdf_url
      : certificateData?.download_presentation;

    const downloadFilename = (() => {
      if (isPdfDownload) {
        const base =
          certificateData?.certificate_name ||
          certificateData?.credential_id ||
          "certificate";
        return `${base}`.replaceAll("/", "-") + ".pdf";
      }
      return (
        certificateData?.download_presentation_filename ||
        (certificateData?.credential_id
          ? `${certificateData.credential_id}.png`
          : "certificate.png")
      );
    })();

    if (downloadUrl) {
      try {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = downloadFilename;
        document.body.appendChild(link);

        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        Toast({
          type: "error",
          title: t("history:error"),
          message: t("history:failed_download_certificate"),
        });
        try {
          if (!isPdfDownload && certificateData?.credential_id) {
            const fallbackUrl = `${EntityConstants.DOWNLOAD_SVG_AS_PNG}${certificateData.credential_id}`;
            const fallbackLink = document.createElement("a");
            fallbackLink.href = fallbackUrl;
            fallbackLink.download = downloadFilename;
            document.body.appendChild(fallbackLink);
            fallbackLink.click();
            document.body.removeChild(fallbackLink);
            return;
          }
        } catch (e) {
          // ignore
        }

        const fallbackLink = document.createElement("a");
        fallbackLink.href = downloadUrl;
        fallbackLink.download = downloadFilename;
        document.body.appendChild(fallbackLink);
        fallbackLink.click();
        document.body.removeChild(fallbackLink);
      }
    } else {
      Toast({
        type: "error",
        title: t("history:error"),
        message: t("history:download_url_not_available"),
      });
    }
  };

  // Share handlers
  const handleShare = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setIsShareMenuOpen(false);
  };

  const handleCopyLink = async (urlToCopy) => {
    const currentUrl = urlToCopy;
    try {
      await navigator.clipboard.writeText(currentUrl);
      Toast({
        type: "success",
        title: t("history:success"),
        message: t("history:link_copied_to_clipboard"),
      });
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
    setIsShareMenuOpen(false);
  };

  useEffect(() => {
    fetchCertificateData();
  }, [cert_hash]);

  useEffect(() => {
    const pageTitle =
      certificateData?.org_name ||
      certificateData?.issued_by ||
      certificateData?.certificate_name;

    if (pageTitle) {
      document.title = pageTitle;
    }
    return () => {
      document.title = "CredIssuer";
    };
  }, [
    certificateData?.org_name,
    certificateData?.issued_by,
    certificateData?.certificate_name,
  ]);

  useEffect(() => {
    const colours = certificateData?.org_domain_colours;
    const primary = colours?.primary_colour?.trim();
    const secondary = colours?.secondary_colour?.trim();
    if (!primary && !secondary) return;

    const root = document.documentElement;

    const normalizeHex = (input) => {
      if (!input) return null;
      const v = input.trim();
      const withHash = v.startsWith("#") ? v : `#${v}`;
      const hex = withHash.slice(1);
      if (/^[0-9a-fA-F]{3}$/.test(hex))
        return `#${hex
          .split("")
          .map((c) => c + c)
          .join("")
          .toLowerCase()}`;
      if (/^[0-9a-fA-F]{6}$/.test(hex)) return `#${hex.toLowerCase()}`;
      return null;
    };

    const hexToRgb = (h) => {
      const hex = normalizeHex(h);
      if (!hex) return null;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { r, g, b };
    };

    const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
    const toHex = ({ r, g, b }) =>
      `#${[r, g, b].map((n) => clamp(n).toString(16).padStart(2, "0")).join("")}`;
    const adjust = (hex, factor) => {
      const rgb = hexToRgb(hex);
      if (!rgb) return null;
      const f = Math.max(-1, Math.min(1, factor));
      const target = f >= 0 ? 255 : 0;
      const amt = Math.abs(f);
      return toHex({
        r: rgb.r + (target - rgb.r) * amt,
        g: rgb.g + (target - rgb.g) * amt,
        b: rgb.b + (target - rgb.b) * amt,
      });
    };

    const previousCssVars = new Map();
    const set = (name, value) => {
      if (!value) return;
      if (!previousCssVars.has(name)) {
        previousCssVars.set(name, root.style.getPropertyValue(name));
      }
      root.style.setProperty(name, value);
    };

    const effectivePrimary = normalizeHex(primary) || normalizeHex(secondary);
    if (effectivePrimary) {
      set("--cf-primary-color", effectivePrimary);
      set("--button-color-primary", effectivePrimary);
      set("--cf-button-color", effectivePrimary);
      set("--dont-have-account-sign-up-text", effectivePrimary);
      set("--issued-screen-tab-button-selected-text-color", effectivePrimary);
      set("--cf-primary-dark-color", adjust(effectivePrimary, -0.12));
      set("--cf-primary-dark-blue-color", adjust(effectivePrimary, -0.2));
      set("--cf-primary-light-color", adjust(effectivePrimary, 0.12));
      set("--cf-primary-card-hover-color", adjust(effectivePrimary, 0.28));
      const rgb = hexToRgb(effectivePrimary);
      if (rgb) {
        set(
          "--button-color-primary-hover",
          `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.86)`,
        );
      }
    }

    const effectiveSecondary = normalizeHex(secondary);
    if (effectiveSecondary) {
      set("--cf-primary-dark-color", effectiveSecondary);
      set("--cf-primary-dark-blue-color", effectiveSecondary);
    }

    return () => {
      previousCssVars.forEach((value, name) => {
        if (value) {
          root.style.setProperty(name, value);
        } else {
          root.style.removeProperty(name);
        }
      });
    };
  }, [certificateData?.org_domain_colours]);

  useEffect(() => {
    setHeaderLogoVisible(true);
  }, [certificateData?.issuer_logo]);

  const hasRedirectUrl = (url) =>
    typeof url === "string" && url.trim().length > 0;

  const handleRedirect = (url) => {
    if (!hasRedirectUrl(url)) return;
    const targetUrl = url.trim();
    const newWindow = window.open(targetUrl, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  const hasLearningOutcomes = certificateData?.learning_outcomes?.length > 0;

  const hasSkills = certificateData?.skills?.length > 0;

  const socialUrls = certificateData?.social_urls || {};

  const issuerWebsiteUrl = certificateData?.issuer_website_url;
  const hasIssuerWebsiteUrl = hasRedirectUrl(issuerWebsiteUrl);
  const courseUrl = certificateData?.course_url;
  const hasCourseUrl = hasRedirectUrl(courseUrl);

  const shareActions = [
    ...(socialUrls.whatsapp_url
      ? [
          {
            id: "whatsapp",
            label: t("history:share_on_whatsapp"),
            className: "bg-green-500 hover:bg-green-600 text-white",
            icon: <FaWhatsapp className="w-5 h-5" />,
            onClick: () => handleShare(socialUrls.whatsapp_url),
          },
        ]
      : []),
    ...(socialUrls.linkedin_url
      ? [
          {
            id: "linkedin",
            label: t("history:share_on_linkedin"),
            className: "bg-blue-700 hover:bg-blue-800 text-white",
            icon: <FaLinkedin className="w-5 h-5" />,
            onClick: () => handleShare(socialUrls.linkedin_url),
          },
        ]
      : []),
    ...(socialUrls.manual_copy_url
      ? [
          {
            id: "copy-link",
            label: t("history:copy_link"),
            className: "bg-gray-600 hover:bg-gray-700 text-white",
            icon: <Link2 className="w-5 h-5" />,
            onClick: () => handleCopyLink(socialUrls.manual_copy_url),
          },
        ]
      : []),
  ];

  const shareArcOffsetsByCount = {
    1: [{ x: -96, y: 0 }],
    2: [
      { x: -110, y: 0 },
      { x: -78, y: 68 },
    ],
    3: [
      { x: -120, y: 0 },
      { x: -94, y: 52 },
      { x: -52, y: 92 },
    ],
  };

  const shareArcOffsets =
    shareArcOffsetsByCount[shareActions.length] || shareArcOffsetsByCount[3];

  const handleKeyDown = (event, callback) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      callback();
    }
  };

  const Shimmer = ({ className = "" }) => (
    <div className={`animate-pulse rounded-lg bg-gray-200 ${className}`} />
  );

  const RecipientSkeleton = () => (
    <div
      className="bg-white rounded-xl shadow-sm overflow-hidden"
      style={{ border: "1px solid #e5e7eb" }}
    >
      <div className="p-5">
        <div className="flex items-center gap-3 mb-5">
          <Shimmer className="w-14 h-14 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Shimmer className="h-3 w-20" />
            <Shimmer className="h-5 w-40" />
            <Shimmer className="h-3 w-32" />
          </div>
        </div>
        <div
          className="space-y-3 pt-4"
          style={{ borderTop: "1px solid #f3f4f6" }}
        >
          <div className="flex justify-between">
            <Shimmer className="h-4 w-20" />
            <Shimmer className="h-4 w-28" />
          </div>
          <div className="flex justify-between">
            <Shimmer className="h-4 w-24" />
            <Shimmer className="h-4 w-28" />
          </div>
          <div className="flex justify-between">
            <Shimmer className="h-4 w-16" />
            <Shimmer className="h-6 w-24 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );

  const IssuerSkeleton = () => (
    <div
      className="bg-white rounded-xl shadow-sm p-5"
      style={{ border: "1px solid #e5e7eb" }}
    >
      <div className="flex items-center gap-3">
        <Shimmer className="w-14 h-14 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Shimmer className="h-3 w-24" />
          <Shimmer className="h-5 w-48" />
          <Shimmer className="h-3 w-28" />
        </div>
      </div>
      <div
        className="mt-4 pt-4 space-y-2"
        style={{ borderTop: "1px solid #f3f4f6" }}
      >
        <Shimmer className="h-4 w-full" />
        <Shimmer className="h-4 w-11/12" />
        <Shimmer className="h-4 w-9/12" />
      </div>
    </div>
  );

  const PreviewSkeleton = () => (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm flex-1 overflow-hidden">
      <div className="w-full h-full min-h-[15rem] bg-gray-100 border border-gray-200 rounded-xl flex items-center justify-center">
        <div className="w-full h-full p-6 flex flex-col items-center justify-center gap-4">
          <Shimmer className="w-3/4 h-8 rounded" />
          <Shimmer className="w-1/2 h-4 rounded" />
          <Shimmer className="w-2/3 h-6 rounded mt-2" />
          <Shimmer className="w-4/5 h-32 rounded-xl mt-2" />
          <Shimmer className="w-1/2 h-4 rounded mt-2" />
          <Shimmer className="w-2/3 h-4 rounded" />
        </div>
      </div>
    </div>
  );

  const StatusBadge = ({ status }) => {
    const tone = getCredentialStatusTone(status);
    const normalizedStatus = normalizeCredentialStatus(status);
    const config = {
      badgeClass: STATUS_TONE_STYLES[tone].badgeClass,
      icon:
        normalizedStatus === "revoked" ? (
          <Ban className="w-3.5 h-3.5" />
        ) : normalizedStatus === "expired" ? (
          <XCircle className="w-3.5 h-3.5" />
        ) : normalizedStatus === "not_verified" ||
          normalizedStatus === "unknown" ? (
          <AlertCircle className="w-3.5 h-3.5" />
        ) : (
          <CheckCircle2 className="w-3.5 h-3.5" />
        ),
      label: status || t("history:undefined"),
    };

    return (
      <button
        type="button"
        id="credential-status-badge"
        onClick={() => setShowStatusDetails(true)}
        aria-label={t("history:view_verification_details", {
          status: config.label,
          defaultValue: `View verification details: ${config.label}`,
        })}
        className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2 ${config.badgeClass}`}
      >
        {config.icon}
        {config.label}
      </button>
    );
  };

  if (loading) {
    return (
      <div
        className="w-full px-4 pb-4 md:px-8 min-h-screen flex flex-col justify-start font-sans text-gray-900 bg-cfSecondary overflow-x-hidden"
        id={pageRootTestIds.credentialPreview}
      >
        <header className="pb-2 mb-6">
          <Shimmer className="w-16 h-16 rounded mt-2" />
          <Shimmer className="h-7 w-56 mt-4" />
        </header>

        <div className="w-full flex flex-col md:flex-row gap-6">
          <div className="flex flex-col w-full lg:w-[40%] gap-5">
            <RecipientSkeleton />
            <IssuerSkeleton />
          </div>

          <div className="w-full lg:w-[60%] flex flex-col gap-5">
            <PreviewSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cfSecondary">
        <div className="text-red-600 text-center p-4">{error}</div>
      </div>
    );
  }

  return (
    <div
      id={pageRootTestIds.credentialPreview}
      className="w-full px-4 pb-4 md:px-8 min-h-screen flex flex-col justify-start font-sans text-gray-900 bg-cfSecondary overflow-x-hidden"
    >
      <EvidenceModal
        isOpen={showMarksheets}
        toggleModal={setShowMarksheets}
        marksheets={certificateData.evidence || []}
      />
      <ImagePreviewModal
        isOpen={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        imageUrl={certificateData.artifact_url || certificateData.svg_string}
        artifactType={certificateData.artifact_type}
        downloadUrl={certificateData.download_presentation}
        downloadFilename={certificateData.download_presentation_filename}
        credentialId={certificateData.credential_id}
      />
      <CredentialStatusDetailsModal
        isOpen={showStatusDetails}
        onClose={() => setShowStatusDetails(false)}
        encodedData={cert_hash}
        fallbackStatus={certificateData?.status}
        onResolved={setResolvedStatus}
      />

      <header className="pb-2 mb-6">
        {certificateData.issuer_logo && headerLogoVisible && (
          <button
            type="button"
            className={`mb-3 mt-3 flex items-center ${hasIssuerWebsiteUrl ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2" : "cursor-default"}`}
            onClick={() => handleRedirect(issuerWebsiteUrl)}
            disabled={!hasIssuerWebsiteUrl}
          >
            <img
              src={certificateData.issuer_logo}
              alt={
                certificateData.org_name ||
                certificateData.issued_by ||
                t("history:issuer_logo")
              }
              className="h-16 max-w-full object-contain"
              onError={() => setHeaderLogoVisible(false)}
            />
          </button>
        )}
        <div className="w-full border-b border-gray-200 " />
        <h2 className="text-2xl mt-4 font-bold">
          {certificateData.certificate_name}
        </h2>
      </header>

      <div className="w-full flex flex-col md:flex-row gap-6">
        <div
          ref={leftPanelRef}
          className="flex flex-col items-stretch w-full lg:w-[40%] gap-5"
        >
          <div
            className="bg-white rounded-xl shadow-sm overflow-hidden order-1"
            style={{ border: "1px solid #e5e7eb" }}
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-14 h-14 border-2 border-cfPrimary bg-cfSecondary rounded-full flex-shrink-0 flex items-center justify-center">
                  <UserRound className="w-7 h-7 text-cfPrimary" />
                </div>
                <div className="flex flex-col min-w-0">
                  <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-0.5 text-cfPrimary">
                    {t("history:awarded_to")}
                  </p>
                  <p className="font-bold text-lg text-gray-900 leading-tight truncate">
                    {certificateData.recipient_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("history:id")} : {certificateData.credential_id}
                  </p>
                </div>
              </div>

              <div
                className="space-y-3 pt-4"
                style={{ borderTop: "1px solid #f3f4f6" }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {t("history:issued_on")}
                  </span>
                  <span className="text-sm font-bold text-cfPrimary">
                    {certificateData.issued_at}
                  </span>
                </div>
                {certificateData?.expires_on && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {t("history:expiry_date")}
                    </span>
                    <span className="text-sm italic text-gray-400">
                      {certificateData.expires_on}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {t("history:status")}
                  </span>
                  {resolvedStatus ? (
                    <StatusBadge status={resolvedStatus} />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowStatusDetails(true)}
                      className="inline-flex items-center gap-2 rounded-xl border border-cfPrimary/20 bg-cfSecondary px-3.5 py-1.5 text-sm font-bold text-cfPrimary shadow-sm transition hover:border-cfPrimary/40 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2"
                    >
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-cfPrimary/10">
                        <CheckCircle2 className="h-3 w-3 text-cfPrimary" />
                      </span>
                      {t("history:verify_credential", {
                        defaultValue: "Verify Credential",
                      })}
                    </button>
                  )}
                </div>
              </div>

              {certificateData.evidence &&
                certificateData.evidence.filter((item) => item.evidence_url)
                  .length > 0 && (
                  <div
                    className="mt-4 pt-3"
                    style={{ borderTop: "1px solid #f3f4f6" }}
                  >
                    <button
                      onClick={() => setShowMarksheets(true)}
                      className="w-full px-4 py-2.5 bg-cfPrimary text-cfSecondary rounded-lg font-medium flex items-center justify-center space-x-2 hover:bg-cfPrimaryDark transition duration-200"
                    >
                      <span>{t("history:view_certificates")}</span>
                    </button>
                  </div>
                )}
            </div>
          </div>

          <div
            className="bg-white rounded-xl shadow-sm p-5 order-2"
            style={{ border: "1px solid #e5e7eb" }}
          >
            <div className="flex items-center gap-3">
              <button
                type="button"
                className={`w-14 h-14 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center ${hasIssuerWebsiteUrl ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2" : "cursor-default"}`}
                style={{
                  border: "2px solid #e5e7eb",
                  backgroundColor: "#f3f4f6",
                }}
                onClick={() => handleRedirect(issuerWebsiteUrl)}
                disabled={!hasIssuerWebsiteUrl}
              >
                <img
                  src={certificateData.issuer_logo}
                  alt={t("history:issuer_logo")}
                  className="w-full h-full object-contain"
                />
              </button>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-0.5 text-cfPrimary">
                  {t("history:issuer_details")}
                </p>
                <button
                  type="button"
                  className={`font-bold text-lg text-gray-900 truncate text-start ${hasCourseUrl ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2" : "cursor-default"}`}
                  onClick={() => handleRedirect(courseUrl)}
                  disabled={!hasCourseUrl}
                >
                  {certificateData.programme_display_name}
                </button>
                <p className="text-xs text-gray-500 mt-0.5">
                  {certificateData.issued_by}
                </p>
              </div>
            </div>

            {certificateData.programme_description && (
              <div
                className="mt-4 pt-4"
                style={{ borderTop: "1px solid #f3f4f6" }}
              >
                <p className="text-sm text-gray-600 leading-relaxed">
                  {certificateData.programme_description}
                </p>
              </div>
            )}

            {hasCourseUrl && (
              <button
                onClick={() => handleRedirect(courseUrl)}
                className="mt-4 px-5 py-2 bg-cfPrimary text-cfSecondary rounded-lg font-medium flex w-fit items-center justify-center space-x-2 hover:bg-cfPrimaryDark transition duration-200"
              >
                <span>{t("history:know_more")}</span>
              </button>
            )}

            {(certificateData.issuer_url ||
              certificateData.accreditation_url) && (
              <div
                className="mt-4 pt-3 flex flex-col gap-2"
                style={{ borderTop: "1px solid #f3f4f6" }}
              >
                {certificateData.issuer_url && (
                  <button
                    type="button"
                    onClick={() => handleRedirect(certificateData.issuer_url)}
                    className="text-sm font-medium cursor-pointer focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2 text-start w-fit flex items-center gap-1.5 hover:underline text-cfPrimary"
                  >
                    <svg
                      className="w-3.5 h-3.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    {t("history:official_issuer_org")}
                  </button>
                )}
                {certificateData.accreditation_url && (
                  <button
                    type="button"
                    onClick={() =>
                      handleRedirect(certificateData.accreditation_url)
                    }
                    className="text-sm font-medium cursor-pointer focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2 text-start w-fit flex items-center gap-1.5 hover:underline text-cfPrimary"
                  >
                    <svg
                      className="w-3.5 h-3.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    {t("history:verify_accreditation")}
                  </button>
                )}
              </div>
            )}

            {hasLearningOutcomes && (
              <div
                className="mt-4 pt-4"
                style={{ borderTop: "1px solid #f3f4f6" }}
              >
                <p className="text-[11px] font-bold tracking-[0.12em] uppercase mb-3 text-cfPrimary">
                  {t("history:learning_outcomes_header")}
                </p>
                <div className="space-y-2">
                  {certificateData.learning_outcomes.map((outcome, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 py-2 px-3 rounded-lg"
                      style={{ border: "1px solid #e5e7eb" }}
                    >
                      <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 bg-cfPrimary">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="white"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="3"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed">
                        {outcome}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {!hasLearningOutcomes && hasSkills && (
            <div
              className="bg-white rounded-xl shadow-sm p-5 order-3"
              style={{ border: "1px solid #e5e7eb" }}
            >
              <div
                className="flex items-center gap-2.5 pb-3"
                style={{ borderBottom: "1px solid #f3f4f6" }}
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-cfPrimary">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="font-bold text-lg text-gray-900">
                  {t("history:skills_acquired")}
                </h3>
              </div>
              <div className="pt-3">
                <div className="flex flex-wrap gap-2">
                  {certificateData.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium text-cfPrimary bg-[#f0f5ff] hover:bg-[#e0eaff] transition-colors"
                      style={{ border: "1px solid #d1e0ff" }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ====== RIGHT PANEL ====== */}
        <div
          ref={rightPanelRef}
          className="w-full lg:w-[60%] flex flex-col gap-5 order-1 lg:order-2 overflow-hidden"
        >
          <div className="border border-gray-200 rounded-xl bg-white shadow-sm flex flex-col justify-between relative group flex-1 overflow-hidden">
            <div className="w-full h-full min-h-[15rem] bg-gray-100 text-center border border-gray-200 rounded-xl overflow-hidden flex items-center justify-center">
              {isPdf ? (
                isPdfLoading ? (
                  <div className="flex items-center justify-center w-full h-full">
                    <CfLoader />
                  </div>
                ) : (
                  <canvas
                    ref={pdfCanvasRef}
                    className="max-w-full max-h-full"
                  />
                )
              ) : (
                <>
                  {imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                      <CfLoader />
                    </div>
                  )}
                  <CertificateArtwork
                    url={
                      certificateData.artifact_url || certificateData.svg_string
                    }
                    artifactType={certificateData.artifact_type}
                    alt={t("history:certificate_preview")}
                    className={`object-contain w-full h-full transition-opacity duration-300 ${imageLoading ? "opacity-0" : "opacity-100"}`}
                    maxHeightVh={75}
                    onLoad={() => setImageLoading(false)}
                    onError={() => setImageLoading(false)}
                  />
                  <button
                    className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/30 opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2"
                    onClick={() => setShowImagePreview(true)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => setShowImagePreview(true))
                    }
                    aria-label={t("history:preview_certificate_full")}
                    type="button"
                  >
                    <div className="flex flex-col items-center">
                      <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                        <div className="bg-cfSecondary bg-opacity-80 rounded-full p-3">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 text-gray-700"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                            />
                          </svg>
                        </div>
                      </div>
                      <span className="text-cfSecondary font-medium">
                        {t("history:preview")}
                      </span>
                    </div>
                  </button>

                  {/* Share Button */}
                  {shareActions.length > 0 && (
                    <button
                      id="credential-preview-share-button"
                      ref={shareButtonRef}
                      className="absolute top-2 right-14 bg-cfSecondary rounded-full p-2 shadow-md cursor-pointer focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2 z-50"
                      onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
                      onKeyDown={(e) =>
                        handleKeyDown(e, () =>
                          setIsShareMenuOpen(!isShareMenuOpen),
                        )
                      }
                      aria-label={t("history:share_certificate")}
                      type="button"
                    >
                      <Share2 className="h-5 w-5 text-gray-700" />
                    </button>
                  )}

                  {/* Share Menu */}
                  <div
                    ref={shareMenuRef}
                    className="absolute top-3 right-14 w-0 h-0 z-[80] pointer-events-none"
                    aria-hidden={!isShareMenuOpen}
                  >
                    {shareActions.map((action, index) => {
                      const offset = shareArcOffsets[index] || {
                        x: -94,
                        y: 64,
                      };
                      return (
                        <button
                          key={action.id}
                          id={`credential-preview-share-${action.id}`}
                          onClick={action.onClick}
                          className={`absolute top-0 left-0 p-3 rounded-full shadow-lg ${action.className} focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2 pointer-events-auto`}
                          aria-label={action.label}
                          tabIndex={isShareMenuOpen ? 0 : -1}
                          style={{
                            transform: isShareMenuOpen
                              ? `translate(${offset.x}px, ${offset.y}px) scale(1)`
                              : "translate(0, 0) scale(0.4)",
                            opacity: isShareMenuOpen ? 1 : 0,
                            visibility: isShareMenuOpen ? "visible" : "hidden",
                            transitionProperty:
                              "transform, opacity, visibility",
                            transitionDuration: "320ms, 240ms, 0ms",
                            transitionTimingFunction:
                              "cubic-bezier(0.22, 1, 0.36, 1), ease-out, linear",
                            transitionDelay: isShareMenuOpen
                              ? `${index * 45}ms`
                              : `${(shareActions.length - index - 1) * 35}ms`,
                          }}
                          type="button"
                        >
                          {action.icon}
                        </button>
                      );
                    })}
                  </div>

                  {/* Download Button */}
                  <button
                    id="credential-preview-download-button"
                    className="absolute top-2 right-2 bg-cfSecondary rounded-full p-2 shadow-md cursor-pointer focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2 z-10"
                    onClick={handleDownload}
                    onKeyDown={(e) => handleKeyDown(e, handleDownload)}
                    aria-label={t("history:download_certificate")}
                    type="button"
                  >
                    <DownloadIcon
                      size={20}
                      className="inline-flex"
                      aria-hidden
                    />
                  </button>
                </>
              )}
            </div>
          </div>

          {hasSkills && hasLearningOutcomes && (
            <div
              className="bg-white rounded-xl shadow-sm p-5"
              style={{ border: "1px solid #e5e7eb" }}
            >
              <div
                className="flex items-center gap-2.5 pb-3"
                style={{ borderBottom: "1px solid #f3f4f6" }}
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-cfPrimary">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="font-bold text-lg text-gray-900">
                  {t("history:skills_acquired")}
                </h3>
              </div>
              <div className="pt-3">
                <div className="flex flex-wrap gap-2">
                  {certificateData.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium text-cfPrimary bg-[#f0f5ff] hover:bg-[#e0eaff] transition-colors"
                      style={{ border: "1px solid #d1e0ff" }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CredentialPreview;
